import { AlertCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export interface IncompleteDataBadgeProps {
  className?: string;
}

/**
 * `<IncompleteDataBadge>` — small coral pill warning that a composite
 * score was renormalized due to missing sub-scores (D4 cold-start UX).
 *
 * Anatomy + copy locked by UI-SPEC § 3.10 + § 8:
 * - Visual: `bg-coral-500/20 border border-coral-500/40 text-coral-100`
 *   with a 12px lucide `AlertCircle` icon.
 * - Text:   "Incomplete data"
 * - Tooltip: "This score uses available signals only. Walkability or
 *            entertainment data is still arriving."
 *
 * ARIA: `role="status" aria-label="..."` per UI-SPEC § 9.3 — announces
 * the meaning to screen-reader users without firing a live-region update.
 */
export function IncompleteDataBadge({ className }: IncompleteDataBadgeProps) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="status"
            aria-label="Incomplete data: this score uses available signals only"
            className={cn(
              'inline-flex h-6 px-2 gap-1 items-center rounded-full text-xs font-semibold',
              'bg-coral-500/20 border border-coral-500/40 text-coral-100',
              'cursor-help',
              className,
            )}
          >
            <AlertCircle aria-hidden className="w-3 h-3" />
            Incomplete data
          </span>
        </TooltipTrigger>
        <TooltipContent
          className="bg-ocean-950/95 text-foam-50 border-foam-50/10 max-w-[280px] text-xs"
          sideOffset={6}
        >
          This score uses available signals only. Walkability or entertainment data is still arriving.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
